import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Redirect if already logged in
    if (user) {
      navigate('/dashboard');
    }

    // Load Google Sign-In script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });

        window.google.accounts.id.renderButton(
          document.getElementById('googleSignInButton'),
          { 
            theme: 'filled_blue',
            size: 'large',
            width: 350,
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left'
          }
        );
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, [user, navigate]);

  const handleCredentialResponse = (response) => {
    setIsLoading(true);
    setError('');

    try {
      // Decode JWT token to get user info
      const credential = response.credential;
      const payload = JSON.parse(atob(credential.split('.')[1]));

      // Check if email is from allowed domain
      if (!payload.email.endsWith('@ivalue.co.in')) {
        setError('Access denied. Only @ivalue.co.in email addresses are allowed.');
        setIsLoading(false);
        return;
      }

      const userData = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
        token: credential,
        loginTime: new Date().toISOString(),
      };

      login(userData);
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to authenticate with Google. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="mx-auto h-16 w-20 rounded-2xl bg-[var(--gradient-trust)] ring-1 ring-white/10 mb-4 flex items-center justify-center p-2" >
                <img src="https://cdn.ivaluetv.com/public/ivalue%20transparent%20white.png" className='w-full h-full object-contain' alt="iValue Logo" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "var(--font-display)" }}>
              Welcome Back
            </h1>
            <p className="text-[var(--text-secondary)] text-sm">
              Sign in to access your Identity Security Dashboard
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 rounded-xl bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}

          {/* Google Sign-In Button */}
          <div className="flex flex-col items-center space-y-4">
            <div 
              id="googleSignInButton" 
              className="flex justify-center"
            />
            
            {isLoading && (
              <div className="text-sm text-[var(--text-secondary)]">
                Authenticating...
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center">
            <p className="text-xs text-[var(--text-secondary)]">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>

          
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
